import { useTeam } from '@vrplatform/data';
import { LazyTable, type MRT_ColumnDef } from '@vrplatform/table';
import { useMemo } from 'react';
import {
  type OwnerStatementSummariesTableRow,
  useOwnerStatementSummariesTableQuery,
} from './useOwnerStatementSummariesTableQuery';
import { OwnerStatementSummariesTableFilter } from './OwnerStatementSummariesTableFilter';
import { Box, Text } from '@mantine/core';
import { day } from '@vrplatform/utils';
import { useOwnerStatementSummariesDetailFilter } from '../detail/useOwnerStatementSummariesDetailFilter';
import { getFinancialAmountColumn } from '../getFinancialAmountColumn.utils';
import { useTableStoreSelecting } from '../../../../stores';

type Column = MRT_ColumnDef<OwnerStatementSummariesTableRow>;

export const OwnerStatementSummariesTable = () => {
  const [{ currency }] = useTeam();
  // const { sorting, setSorting } = useSorting();
  const queryData = useOwnerStatementSummariesTableQuery();

  const { setFilter, filter } = useOwnerStatementSummariesDetailFilter();

  const rows = queryData.data?.rows ?? [];
  const selecting = useTableStoreSelecting<OwnerStatementSummariesTableRow>();

  const totals = queryData.data?.totals ?? {
    balanceEnd: 0,
    balanceStart: 0,
    payouts: 0,
    netRevenue: 0,
    expenses: 0,
    netIncome: 0,
    currentBalance: 0,
    reserve: 0,
  };

  const columns = useMemo<Column[]>(() => {
    return [
      {
        header: 'Owner',
        Cell: ({ row }) => {
          const contact = row.original.contact;

          return (
            <Box>
              <Text>{contact.formattedName}</Text>
              <Text size="xs" c="gray">
                {contact.email}
              </Text>
            </Box>
          );
        },
      },

      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        id: 'Balance start',
        currency,
        header: 'Balance start',
        amountKey: ['balanceStart'],
        totals,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        id: 'Payouts',
        currency,
        header: 'Payouts',
        amountKey: ['payouts'],
        totals,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        id: 'Balance forward',
        currency,
        header: 'Balance forward',
        amountKey: ['balanceStart', 'payouts'],
        totals,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        currency,
        header: 'Net revenue',
        amountKey: ['netRevenue'],
        totals,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        currency,
        header: 'Expenses',
        amountKey: ['expenses'],
        totals,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        id: 'Balance end',
        currency,
        header: 'Balance end',
        amountKey: ['balanceEnd'],
        totals,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        id: 'Reserve',
        currency,
        header: 'Reserve',
        amountKey: ['reserve'],
        totals,
        showNegative: true,
      }),
      getFinancialAmountColumn<OwnerStatementSummariesTableRow>({
        id: 'Available balance',
        currency,
        header: 'Available balance',
        amountKey: ['balanceEnd', 'reserve'],
        totals,
        type: 'subtract',
      }),
    ];
  }, [currency, totals]);

  return (
    <LazyTable
      resetFilter={() =>
        setFilter({
          monthRange: `${day().startOf('year').yyyymmdd()}...${day().endOf('year').yyyymmdd()}`,
        })
      }
      data={{
        error: queryData.error?.message,
        loading: queryData.isLoading,
        rows,
        rowCount: rows.length,
        isFetching: queryData.isFetching,
      }}
      table={{
        columns,
        enableStickyFooter: true,
        onRowClick: {
          handler: (row) => {
            setFilter({
              contactId: row.original.contact.contactId,
              monthRange: filter.monthRange,
            });
          },
        },
      }}
      selecting={selecting}
    >
      <OwnerStatementSummariesTableFilter />
    </LazyTable>
  );
};
